<template>
	<QuickPillList
		v-if="mostPopularPlaces.length > 0"
		:header-props="mostPopularPlacesTitle"
		:pills="mostPopularPlaces"
		:currency="currency"
	/>
</template>

<script lang="ts" setup>
import type { AxiosError } from 'axios';
import axios from 'axios';
import request from '@components/common/services/request';
import type { SearchFormDataType } from '@/interfaces/search-form';
import type MostPopularPill from '@/interfaces/api/v1-best-package-offer-for-geo-location';
import { addEnabledParams } from '@services/url';
import type { HeaderProps } from '@lmt-rpb/ComponentHeader/componentHeaderTypes';
import type { QuickPillType } from '@lmt-rpb/QuickPillList/types';
import type { HotelListData } from '@/interfaces/components/hotelListData';
import QuickPillList from '@lmt-rpb/QuickPillList/QuickPillList.vue';
import { computed, onMounted, ref } from 'vue';
import { useStore } from '@/components/common/store';
import { getDefaultCurrency, isClientSwitzerland } from '@utils/environmentUtils';
import { API_URLS } from '@global-js/constants';
import type { ApiDataTypes, ApiDataTypesKeys } from '../common/types';

const store = useStore();

interface QuickPillHotelListType extends QuickPillType {
	responseUrl: string;
}

interface Props {
	hotelListData: HotelListData;
}

const props = defineProps<Props>();

const mostPopularPlaces = ref<QuickPillHotelListType[]>([]);

const isCh = isClientSwitzerland();

const currency = getDefaultCurrency();

const urlaubsorte = isCh ? 'Ferienorte' : 'Urlaubsorte';

const title = `Die beliebtesten ${props.hotelListData?.locationType === 'REGIONGROUP' ? 'Regionen' : urlaubsorte}`;

const mostPopularPlacesTitle = ref<HeaderProps>({
	title,
	mobileTitle: title,
	subtitle: `Hotels in ${props.hotelListData?.destinationName}'s beliebtesten ${props.hotelListData?.locationType === 'REGIONGROUP' ? 'Regionen' : 'Orten'} günstig buchen`,
});

const formData = computed((): SearchFormDataType => store.state.searchMask);

const apiData = store.getters['searchMask/apiData'];

const fetchGeoLocationUrl = computed(() => (formData.value.onlyHotel ? API_URLS.GEO_LOCATION.HOTELS : API_URLS.GEO_LOCATION.PACKAGES));

const getMostPopularPlacesParams = (listType: string) => {
	const bodyParams: Partial<SearchFormDataType> = {
		regionGroupIds: listType === 'REGIONGROUP' ? [parseInt(props.hotelListData?.rlId, 10)] : undefined,
		rid: listType !== 'REGIONGROUP' ? [parseInt(props.hotelListData?.rid, 10)] : undefined,
		sortingPricePopular: formData.value.sortingPricePopular ? 'Popular' : 'Price',
	};
	// Cities needs to be excluded for Regiongroup & Region because the 'smallest' param wins
	const excludes: ApiDataTypesKeys[] = ['Cities'];
	// Same goes for Region & Regions with Regiongroup requests
	if (listType === 'REGIONGROUP') excludes.push('Region', 'Regions');
	const params = apiData(bodyParams, excludes);
	params.Region = params.Regions;
	params.NumberOfResults = 5;
	return params;
};

const addAllRegionPill = () => {
	if (props.hotelListData?.locationType === 'REGIONGROUP') {
		const regionUrl = `/region/g/${props.hotelListData?.rlId}/`;
		if (props.hotelListData?.locationType === 'REGIONGROUP') {
			const allRegionsPill: QuickPillHotelListType = {
				destination: `Alle ${props.hotelListData?.destinationName} Regionen`,
				titleTag: `Alle ${props.hotelListData?.destinationName} Regionen`,
				url: '',
				responseUrl: regionUrl,
			};
			mostPopularPlaces.value.push(allRegionsPill);
		}
	}
};

const addUrlParameter = () => {
	mostPopularPlaces.value.forEach((pill) => {
		const ignoredParams = ['cyid', 'rid'];

		pill.url = addEnabledParams(pill.responseUrl, ignoredParams);
	});
};

const fetchMostPopularPlaces = (params: ApiDataTypes, responseIndex: number): void => {
	request<MostPopularPill>({ url: fetchGeoLocationUrl.value, data: params })
		.then((resp) => {
			if (!resp?.[responseIndex]) return;
			resp[responseIndex].forEach((pill) => {
				const place: QuickPillHotelListType = {
					destination: pill.Name,
					price: pill.Pricing.Total?.Converted?.toString() ?? pill.Pricing.Total?.Amount?.toString(),
					responseUrl: pill.URL,
					currency: params.CurrencyCode,
					url: '',
					titleTag: pill.Name,
				};
				mostPopularPlaces.value.push(place);
			});
			addAllRegionPill();
			addUrlParameter();
		})
		.catch((error: AxiosError) => {
			if (!axios.isCancel(error)) {
				console.error('GeoLocation: ', error);
			}
		});
};

const loadMostPopularPlaces = (): void => {
	const listType = props.hotelListData?.locationType;
	mostPopularPlaces.value = [];
	if (listType !== undefined && listType !== 'CITY') {
		const params = getMostPopularPlacesParams(listType);
		if (params.Region && Number.isInteger(params.Region[0])) {
			fetchMostPopularPlaces(params, params.Region);
		} else if (params.RegionGroups && Number.isInteger(params.RegionGroups[0])) {
			fetchMostPopularPlaces(params, params.RegionGroups[0]);
		}
	}
};

onMounted((): void => {
	loadMostPopularPlaces();
});

defineExpose({
	loadMostPopularPlaces,
});
</script>
